/**
 * Enrollment Task Form
 *
 * Renders the provider task(s) for a PROVIDER_ACTION_REQUIRED enrollment and
 * lets the user complete them in-app — including DOCUMENT fields, which upload
 * the file to Stedi and submit the resulting documentId. Self-gating: renders
 * nothing when the enrollment has no open tasks. Shared by the payer enrollment
 * group and the setup-wizard enrollment card.
 */

import { useState } from 'react';
import type { Enrollment } from '@features/enrollments';
import {
  useCompleteEnrollmentTaskMutation,
  useUploadEnrollmentDocumentMutation,
} from '@features/enrollments';
import { Chip } from '@jotpsych/ui';
import {
  CloudUploadOutlined as CloudUploadIcon,
  PendingActionsOutlined as PendingActionsIcon,
} from '@mui/icons-material';
import { alpha, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../utils/errorUtils';

interface EnrollmentTaskField {
  key: string;
  label?: string;
  type?: string;
}

interface EnrollmentTask {
  id: string;
  title?: string;
  isComplete?: boolean;
  responsibleParty?: string;
  fields?: EnrollmentTaskField[];
}

const isDocumentField = (field: EnrollmentTaskField): boolean =>
  (field.type ?? 'TEXT').toUpperCase() === 'DOCUMENT';

// Task documents are signed authorization forms — a few pages of PDF/image at
// most. Reject outsized files client-side rather than reading the whole thing
// into memory as base64 and only failing once it reaches the server.
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024; // 10 MB

/** Read a File as a base64 string (without the data-URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function EnrollmentTaskForm({ enrollment }: { enrollment: Enrollment }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // tasks are present on the response at runtime but post-date the generated type
  const tasks = (enrollment as unknown as { tasks?: EnrollmentTask[] }).tasks ?? [];
  const openTasks = tasks.filter(task => !task.isComplete);
  const enrollmentId = enrollment.stedi_transaction_enrollment_id;

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fileValues, setFileValues] = useState<Record<string, File>>({});
  const [submittingTaskId, setSubmittingTaskId] = useState<string | null>(null);
  const [completeTask] = useCompleteEnrollmentTaskMutation();
  const [uploadDocument] = useUploadEnrollmentDocumentMutation();

  // Only surface task completion while the enrollment is actually awaiting
  // provider action — terminal enrollments can carry stale task records.
  if (
    enrollment.status !== 'PROVIDER_ACTION_REQUIRED' ||
    openTasks.length === 0 ||
    !enrollmentId
  ) {
    return null;
  }

  const stateKey = (taskId: string, fieldKey: string) => `${taskId}:${fieldKey}`;

  // A task is submittable once every field is provided (text filled, file chosen).
  const isTaskReady = (task: EnrollmentTask) =>
    (task.fields ?? []).every(field => {
      const key = stateKey(task.id, field.key);
      return isDocumentField(field)
        ? Boolean(fileValues[key])
        : Boolean((fieldValues[key] ?? '').trim());
    });

  const handleSubmitTask = async (task: EnrollmentTask) => {
    setSubmittingTaskId(task.id);
    try {
      const values: Array<Record<string, unknown>> = [];
      for (const field of task.fields ?? []) {
        const key = stateKey(task.id, field.key);
        if (isDocumentField(field)) {
          const file = fileValues[key];
          if (!file) continue;
          const data = await fileToBase64(file);
          const { document_id } = await uploadDocument({
            enrollmentId,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            data,
          }).unwrap();
          values.push({ key: field.key, documentId: document_id });
        } else {
          values.push({ key: field.key, value: fieldValues[key] ?? '' });
        }
      }
      await completeTask({ enrollmentId, taskId: task.id, values }).unwrap();
      enqueueSnackbar('Task submitted to the payer', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(extractApiErrorMessage(error, 'Failed to submit task. Please try again.'), {
        variant: 'error',
      });
    } finally {
      setSubmittingTaskId(null);
    }
  };

  const accentColor = theme.palette.warning.main;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(accentColor, 0.05),
        border: `1px solid ${alpha(accentColor, 0.25)}`,
      }}
    >
      {openTasks.map((task, index) => {
        const submitting = submittingTaskId === task.id;
        return (
          <Box
            key={task.id}
            sx={index > 0 ? { mt: 2, pt: 2, borderTop: `1px solid ${alpha(accentColor, 0.15)}` } : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: alpha(accentColor, 0.12),
                  color: accentColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PendingActionsIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                  {task.title || 'Action required'}
                </Typography>
                <Chip
                  color="warning"
                  label="Action needed"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}
                />
              </Box>
            </Box>
            <Stack spacing={1.5}>
              {(task.fields ?? []).map(field => {
                const key = stateKey(task.id, field.key);
                if (isDocumentField(field)) {
                  const file = fileValues[key];
                  return (
                    <Box
                      key={field.key}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                    >
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={submitting}
                        sx={{
                          color: 'text.secondary',
                          borderColor: 'divider',
                          '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                        }}
                      >
                        {field.label || field.key}
                        <input
                          type="file"
                          hidden
                          accept="application/pdf,image/*"
                          aria-label={field.label || field.key}
                          onChange={event => {
                            const selected = event.target.files?.[0];
                            event.target.value = '';
                            if (!selected) return;
                            if (selected.size > MAX_DOCUMENT_BYTES) {
                              enqueueSnackbar(
                                `${selected.name} is too large (max 10 MB).`,
                                { variant: 'error' },
                              );
                              return;
                            }
                            setFileValues(prev => ({ ...prev, [key]: selected }));
                          }}
                        />
                      </Button>
                      {file && (
                        <Chip
                          color="success"
                          label={file.name}
                          sx={{ maxWidth: '100%' }}
                        />
                      )}
                    </Box>
                  );
                }
                return (
                  <TextField
                    key={field.key}
                    size="small"
                    label={field.label || field.key}
                    value={fieldValues[key] ?? ''}
                    disabled={submitting}
                    onChange={event =>
                      setFieldValues(prev => ({ ...prev, [key]: event.target.value }))
                    }
                  />
                );
              })}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={submitting || !isTaskReady(task)}
                  onClick={() => handleSubmitTask(task)}
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </Button>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}
